import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIsPromptSupportOptions } from "redux/SingleDataset/selectors";
import { updateModelPrompt } from "redux/Enrichment/actions";

import classNames from "classnames";
import { ModelTypes, EnrichmentModelForPreview } from "types";

import { getVendorLogo } from "helpers/utility/utilities";
import { displayForamttedDate } from "helpers/utility/formatters";
import ModelTypeBadge from "views/components/ModelTypeBadge";
import ModelTrashIcon from "assets/icons/ModelTrashIcon";
import CalenderIcon from "assets/icons/CalenderIcon";
import SendInputArrowIcon from "assets/icons/SendInputArrow";

import styles from "./style.module.scss";

export enum PromptStatus {
  Default = "default",
  Active = "active",
  Error = "error",
  Disabled = "disabled",
}

const ModelPromptTextArea = ({
  modelId,
  inputText,
  editableTooltipContent,
  onSubmitPrompt,
  promptStatus,
  placeholder,
  promptCharLimit,
}: {
  modelId: string;
  inputText: string;
  editableTooltipContent: string;
  onSubmitPrompt: () => void;
  promptStatus: PromptStatus;
  promptCharLimit: number;
  placeholder: string;
}): JSX.Element => {
  const [isHoveringOnPrompt, setIsHoveringOnPrompt] = useState(false);
  const dispatch = useDispatch();
  const handleInputChange = (e: any) => {
    const prompt = e.target.value;
    if (prompt.length <= promptCharLimit) {
      dispatch(updateModelPrompt({ modelId, prompt }));
    }
  };

  const inputLableClassNames = classNames(styles.inputLabel, {
    [styles.inputLabelDisabled]: promptStatus === PromptStatus.Disabled,
  });

  const textAreaContainerClassNames = classNames(styles.textAreaContainer, {
    [styles.textAreaContainerDisabled]: promptStatus === PromptStatus.Disabled,
    [styles.textAreaContainerError]: promptStatus === PromptStatus.Error,
  });

  const textAreaClassNames = classNames(styles.inputField, {
    [styles.inputFieldDisabled]: promptStatus === PromptStatus.Disabled,
  });

  return (
    <div>
      <div
        className={styles.inputSection}
        data-tooltip-id="enrichment-prompt-edit-tooltip"
        data-tooltip-content={editableTooltipContent}
        onMouseOver={() => {
          setIsHoveringOnPrompt(true);
        }}
        onMouseLeave={() => setIsHoveringOnPrompt(false)}
      >
        {isHoveringOnPrompt && editableTooltipContent.length > 0 && (
          <div className={styles.editPromptPopup}>{editableTooltipContent}</div>
        )}
        <label className={inputLableClassNames}>
          Input <span className={styles.requireAsterisk}>*</span>{" "}
        </label>
        <div
          className={textAreaContainerClassNames}
          data-testid="preview-model-prompt-text-area-container"
        >
          <textarea
            className={textAreaClassNames}
            placeholder={placeholder}
            value={inputText}
            onInput={handleInputChange}
            maxLength={promptCharLimit}
            rows={4}
            readOnly={promptStatus === PromptStatus.Disabled}
            data-testid="preview-model-prompt-text-area"
          />
          <div className={styles.promptAreaFooter}>
            <div className={styles.charCount}>
              {inputText.length} of {promptCharLimit} characters
            </div>
            <div
              className={classNames(styles.promptArrowIcon, {
                [styles.activeArrowIcon]: promptStatus === PromptStatus.Active,
              })}
              onClick={onSubmitPrompt}
            >
              <SendInputArrowIcon />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.modelDivider}></div>
    </div>
  );
};

interface SingleModelProps {
  model: EnrichmentModelForPreview;
  isSelected: boolean;
  onSelectModel: (modelId: string, newPrompt?: boolean) => void;
  onRemoveModel: (modelId: string) => void;
  missingPrompt?: boolean;
}

const SingleModel = ({
  model,
  isSelected,
  onRemoveModel,
  onSelectModel,
  missingPrompt,
}: SingleModelProps) => {
  const [isHoveringOnDelete, setIsHoveringOnDelete] = useState(false);
  const promptSupportOptions = useSelector((state: any) =>
    getIsPromptSupportOptions(state)
  );
  const inputText = model.metadata?.prompt || "";

  const assetPath = getVendorLogo(model.vendor_logo_uri);
  const vendorLogoFileName = model.vendor_logo_uri.split(".")[0] || "";

  const modelClassNames = classNames(styles.model, {
    [styles.selectedModel]: isSelected,
  });

  const iconClassnames = classNames(
    isSelected ? styles.selectedCircle : styles.unselectedCircle
  );

  const removeTooltipContent =
    model.deletable.reason.length > 0 ? model.deletable.reason : "Remove Model";

  const promptable = model.vqa_prompt && promptSupportOptions.enabled;

  const editableTooltipContent =
    model.editable.reason.length > 0 ? model.editable.reason : "";

  const onSubmitPrompt = () => {
    if (model.editable.value) {
      if (inputText.length > 0) {
        onSelectModel(model.id, true);
      }
    }
  };

  let modelPromptStatus = PromptStatus.Default;
  if (!model.editable.value) {
    modelPromptStatus = PromptStatus.Disabled;
  } else if (missingPrompt) {
    modelPromptStatus = PromptStatus.Error;
  } else if (isSelected) {
    modelPromptStatus = PromptStatus.Active;
  }

  const promptPlaceholder =
    model.type === ModelTypes.CAPTION_IMAGES
      ? "Describe the image"
      : "Describe the object";

  return (
    <div className={modelClassNames} onClick={() => onSelectModel(model.id)}>
      <div className={styles.modelHeader}>
        <div className={styles.icon}>
          <img src={assetPath} alt={vendorLogoFileName} />
        </div>
        <div className={styles.title} title={model.name}>
          {model.name}
        </div>
        {isSelected && <div className={styles.currentSelection}>Preview</div>}
        <div className={styles.selectionButton}>
          <div className={iconClassnames}></div>
        </div>
      </div>

      <ModelTypeBadge modelType={model.type} />

      {promptable ? (
        <ModelPromptTextArea
          modelId={model.id}
          inputText={inputText}
          placeholder={promptPlaceholder}
          promptStatus={modelPromptStatus}
          editableTooltipContent={editableTooltipContent}
          promptCharLimit={promptSupportOptions.maxChars}
          onSubmitPrompt={onSubmitPrompt}
        />
      ) : (
        <div className={styles.modelDivider}></div>
      )}

      <div className={styles.modelListFooter}>
        <div className={styles.releaserDate}>
          {model.release_date && (
            <>
              <div className={styles.calenderIcon}>
                <CalenderIcon />
              </div>
              <div
                className={styles.releaserDateText}
                data-testid="preview-model-date-text"
              >
                Released {displayForamttedDate(model.release_date)}
              </div>
            </>
          )}
        </div>
        <div
          data-tooltip-id="enrichment-remove-model-tooltip"
          data-tooltip-content={removeTooltipContent}
          className={classNames(styles.deleteButton, {
            [styles.selectedDeleteButton]:
              model.deletable.value && (isSelected || isHoveringOnDelete),
          })}
          onMouseOver={() => setIsHoveringOnDelete(true)}
          onMouseLeave={() => setIsHoveringOnDelete(false)}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveModel(model.id);
          }}
          data-testid="preview-model-delete-btn"
        >
          {isHoveringOnDelete && (
            <div className={styles.removeModelPopup}>
              {removeTooltipContent}
            </div>
          )}
          <ModelTrashIcon />
        </div>
      </div>
    </div>
  );
};

export default SingleModel;
